import React, { useState, useEffect, ChangeEvent, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import { useGlobalState } from "../../../../context/useGlobalState";
import TitleSubtitle from "../../../../components/common/TitleSubtitle";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import Dropdown from "../../../../components/common/Dropdown";
import FormGroup from "../../../../components/common/FormGroup";
import { saveTemporaryWaypoint, getWaypointsForTrack } from "../../../../api/waypoint";
import { getScanConfig, getScannableObjects, ScannableObjectResponse } from "../../../../api/progression";
import { getProjects, getProject } from "../../../../api/project";
import { uploadFileToS3 } from "../../../../api/s3";
import i18n from "../../../../i18n";
import stepStyles from "../createStep.module.css";
import formStyles from "../ScanConfig/CreateObject.module.css";

interface ModifyStoryProps {
    navigateToNext: () => void;
}

interface LocalizedContent {
    name: string;
    text: string;
    audioUrl: string;
}

const ModifyStory: React.FC<ModifyStoryProps> = ({ navigateToNext }) => {
    const { storyId } = useParams<{ storyId: string }>();
    const { trackData, updateTrackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const navigate = useNavigate();
    const token = localStorage.getItem("token") || "";
    const { data: globalState } = useGlobalState();

    // Form state
    const [pictures, setPictures] = useState<string[]>([]);
    const [position, setPosition] = useState<number>(1);
    const [selectedObjectId, setSelectedObjectId] = useState<string>("");
    const [objects, setObjects] = useState<ScannableObjectResponse[]>([]);
    const [waypoint, setWaypoint] = useState<any | null>(null);

    // Language tabs state
    const [activeLanguageTab, setActiveLanguageTab] = useState<number>(0);
    const [localizedContent, setLocalizedContent] = useState<Record<string, LocalizedContent>>({});
    const [availableLanguages, setAvailableLanguages] = useState<string[]>([]);

    // Projects for "Add from projects" feature - Text section
    const [projects, setProjects] = useState<any[]>([]);
    const [selectedProjectId, setSelectedProjectId] = useState<string>("");
    const [projectStories, setProjectStories] = useState<any[]>([]);
    const [selectedProjectStoryId, setSelectedProjectStoryId] = useState<string>("");

    // Projects for "Add from projects" feature - Audio section (separate selection)
    const [selectedProjectIdForAudio, setSelectedProjectIdForAudio] = useState<string>("");
    const [projectStoriesForAudio, setProjectStoriesForAudio] = useState<any[]>([]);
    const [selectedProjectStoryIdForAudio, setSelectedProjectStoryIdForAudio] = useState<string>("");

    // Loading states
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [uploadingPicture, setUploadingPicture] = useState<boolean>(false);
    const [uploadingAudio, setUploadingAudio] = useState<boolean>(false);
    const [loadingProjects, setLoadingProjects] = useState<boolean>(false);

    const linkId = trackData?.trackId || trackData?.id;

    // Load waypoint and objects data
    useEffect(() => {
        const loadData = async () => {
            if (!trackData.trackId || !storyId) return;

            try {
                setLoadingData(true);

                // Load scannable objects
                const configResponse = await getScanConfig({ token, trackId: trackData.trackId });
                if (configResponse?.scanConfig) {
                    const objectsResponse = await getScannableObjects({
                        token,
                        scanConfigId: configResponse.scanConfig.id,
                    });
                    if (objectsResponse?.scannableObjects) {
                        setObjects(objectsResponse.scannableObjects.filter((obj) => obj.status));
                    }
                }

                // Load waypoint data (check both definitive and temporary waypoints)
                const waypointsResponse = await getWaypointsForTrack({
                    token,
                    trackId: trackData.trackId,
                });

                // Check definitive waypoints first
                let currentWaypoint = waypointsResponse?.waypoints?.find((wpt: any) => wpt.id === storyId) as any;

                // If not found, check temporary waypoints
                if (!currentWaypoint) {
                    currentWaypoint = waypointsResponse?.temporaryWaypoints?.find(
                        (wpt: any) => wpt.id === storyId || wpt.internalId === storyId,
                    ) as any;
                }

                if (currentWaypoint) {
                    setWaypoint(currentWaypoint);
                    setSelectedObjectId(currentWaypoint.scannableObjectId || "");
                    setPosition(currentWaypoint.position || 1);

                    // Set pictures
                    const picturesArray = currentWaypoint.pictures || [];
                    setPictures(
                        Array.isArray(picturesArray)
                            ? picturesArray.map((p: any) => (typeof p === "string" ? p : p.url || p.id))
                            : [],
                    );

                    // Initialize languages from waypoint localized data
                    // Check both definitive and temporary localized content
                    const definitiveLangs =
                        currentWaypoint.waypointLocalized?.map((loc: any) => loc.language?.toUpperCase()) || [];

                    const temporaryLangs =
                        currentWaypoint.TemporaryWaypointLocalized?.map((loc: any) => loc.language?.toUpperCase()) ||
                        [];

                    const waypointLangs = [...definitiveLangs, ...temporaryLangs];

                    // Merge with track available languages
                    const trackLangs = trackData?.availableLanguages || [trackData?.language || "FR"];
                    const allLangs = Array.from(new Set([...trackLangs, ...waypointLangs])).filter(Boolean) as string[];
                    setAvailableLanguages(allLangs);

                    // Initialize localized content from both sources
                    const initialContent: Record<string, LocalizedContent> = {};
                    allLangs.forEach((lang) => {
                        // Try definitive waypoint localized first
                        const definitiveLocalized = currentWaypoint.waypointLocalized?.find(
                            (loc: any) => loc.language?.toUpperCase() === lang,
                        );

                        // Try temporary waypoint localized
                        const temporaryLocalized = currentWaypoint.TemporaryWaypointLocalized?.find(
                            (loc: any) => loc.language?.toUpperCase() === lang,
                        );

                        initialContent[lang] = {
                            name: definitiveLocalized?.name || temporaryLocalized?.title || "",
                            text: definitiveLocalized?.fullText || temporaryLocalized?.text || "",
                            audioUrl: definitiveLocalized?.audiotrack?.file || temporaryLocalized?.audioUrl || "",
                        };
                    });
                    setLocalizedContent(initialContent);
                }
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setLoadingData(false);
            }
        };

        loadData();
        // fetch when track/story changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [trackData.trackId, storyId, token]);

    // Load projects for "Add from projects" feature
    useEffect(() => {
        const fetchProjects = async () => {
            setLoadingProjects(true);
            try {
                const response = await getProjects({ token });
                if (response) {
                    setProjects(response);
                }
            } catch (error) {
                console.error("Error fetching projects:", error);
            } finally {
                setLoadingProjects(false);
            }
        };

        fetchProjects();
    }, [token]);

    // Load stories from selected project (text section)
    useEffect(() => {
        const fetchProjectStories = async () => {
            if (!selectedProjectId) {
                setProjectStories([]);
                return;
            }

            try {
                const project = await getProject({ token, projectId: selectedProjectId });
                if (project?.audioAI) {
                    setProjectStories(project.audioAI);
                }
            } catch (error) {
                console.error("Error fetching project stories:", error);
            }
        };

        fetchProjectStories();
    }, [selectedProjectId, token]);

    // Load stories from selected project (audio section)
    useEffect(() => {
        const fetchProjectStoriesForAudio = async () => {
            if (!selectedProjectIdForAudio) {
                setProjectStoriesForAudio([]);
                return;
            }

            try {
                const project = await getProject({ token, projectId: selectedProjectIdForAudio });
                if (project?.audioAI) {
                    // Only include stories that have audio
                    setProjectStoriesForAudio(project.audioAI.filter((s: any) => s.audioUrl));
                }
            } catch (error) {
                console.error("Error fetching project stories for audio:", error);
            }
        };

        fetchProjectStoriesForAudio();
    }, [selectedProjectIdForAudio, token]);

    const getObjectLocalizedName = (obj: ScannableObjectResponse) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = obj.scannableObjectLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return localized?.name || obj.scannableObjectLocalized?.[0]?.name || t("unnamed");
    };

    const objectOptions = objects.map((obj) => ({
        value: obj.id,
        label: getObjectLocalizedName(obj),
    }));

    // Check for duplicate position within the same scannableObject (excluding current story)
    const isDuplicatePosition = useMemo(() => {
        if (!selectedObjectId || position < 1) return false;
        const selectedObj = objects.find((obj) => obj.id === selectedObjectId);
        if (!selectedObj?.waypoints) return false;
        // Exclude the current waypoint being edited from the check
        return selectedObj.waypoints.some((wpt: any) => wpt.position === position && wpt.id !== storyId);
    }, [selectedObjectId, position, objects, storyId]);

    const projectOptions = projects.map((p) => ({
        value: p.id,
        label: p.name || t("unnamed"),
    }));

    const storyOptions = projectStories.map((s: any) => ({
        value: s.id,
        label: s.name || s.title || t("unnamed"),
    }));

    const storyOptionsForAudio = projectStoriesForAudio.map((s: any) => ({
        value: s.id,
        label: s.name || s.title || t("unnamed"),
    }));

    const getLanguageName = (code: string) => {
        const lang = globalState?.languagesAI?.find((l: any) => l.code === code || l.value === code);
        return lang?.label || code;
    };

    // Get the creation language (first language, cannot be removed)
    const creationLanguage = availableLanguages[0] || trackData?.language || "FR";
    const currentLanguage = availableLanguages[activeLanguageTab] || creationLanguage;

    // Get languages that can be added (not already in use)
    const unusedLanguages = useMemo(() => {
        const allLanguages = globalState?.languagesAI || [];
        return allLanguages.filter((lang: any) => !availableLanguages.includes(lang.code));
    }, [globalState?.languagesAI, availableLanguages]);

    // State for showing language selector
    const [showLanguageSelector, setShowLanguageSelector] = useState(false);

    const handleLanguageTabChange = (newValue: number) => {
        setActiveLanguageTab(newValue);
    };

    const handleAddLanguage = (langCode: string) => {
        if (!langCode || availableLanguages.includes(langCode)) return;

        // Initialize content for new language first (before updating languages list)
        setLocalizedContent((prev) => ({
            ...prev,
            [langCode]: { name: "", text: "", audioUrl: "" },
        }));

        const newLanguages = [...availableLanguages, langCode];
        setAvailableLanguages(newLanguages);

        // Switch to the new tab
        setActiveLanguageTab(newLanguages.length - 1);
        setShowLanguageSelector(false);
    };

    const handleRemoveLanguage = (langCode: string) => {
        // Cannot remove the creation language
        if (langCode === creationLanguage) return;

        const newLanguages = availableLanguages.filter((l) => l !== langCode);
        setAvailableLanguages(newLanguages);

        // Remove content for this language
        setLocalizedContent((prev) => {
            const updated = { ...prev };
            delete updated[langCode];
            return updated;
        });

        // Adjust active tab if needed
        if (activeLanguageTab >= newLanguages.length) {
            setActiveLanguageTab(Math.max(0, newLanguages.length - 1));
        }
    };

    const handleContentChange = (field: keyof LocalizedContent, value: string) => {
        setLocalizedContent((prev) => ({
            ...prev,
            [currentLanguage]: {
                ...prev[currentLanguage],
                [field]: value,
            },
        }));
    };

    const handlePictureUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingPicture(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setPictures((prev) => [...prev, response.url]);
            }
        } catch (error) {
            console.error("Error uploading picture:", error);
        } finally {
            setUploadingPicture(false);
        }
    };

    const handleRemovePicture = (index: number) => {
        setPictures((prev) => prev.filter((_, i) => i !== index));
    };

    const handleAudioUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingAudio(true);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                handleContentChange("audioUrl", response.url);
            }
        } catch (error) {
            console.error("Error uploading audio:", error);
        } finally {
            setUploadingAudio(false);
        }
    };

    const handleImportTextFromProject = () => {
        if (!selectedProjectStoryId) return;

        const story = projectStories.find((s) => s.id === selectedProjectStoryId);
        if (story) {
            // Import text content only
            handleContentChange("text", story.content || story.text || "");
            // Import name/title if available
            if (story.title) {
                handleContentChange("name", story.title);
            }
        }
    };

    const handleImportAudioFromProject = () => {
        if (!selectedProjectStoryIdForAudio) return;

        const story = projectStoriesForAudio.find((s) => s.id === selectedProjectStoryIdForAudio);
        if (story && story.audioUrl) {
            // Import audio only
            handleContentChange("audioUrl", story.audioUrl);
        }
    };

    const handleCancel = () => {
        navigate(`/create/tour/${linkId}/stories`);
    };

    const handleSave = async () => {
        if (!trackData.trackId || !storyId) return;

        // Check if at least one language has a name
        const hasContent = Object.values(localizedContent).some((c) => c.name.trim());
        if (!hasContent) return;

        setLoading(true);
        try {
            // Get the first language's content as the primary
            const primaryLang = availableLanguages[0];
            const primaryContent = localizedContent[primaryLang];

            await saveTemporaryWaypoint({
                token,
                internalId: waypoint?.internalId || storyId,
                trackId: trackData.trackId,
                waypointId: storyId,
                language: primaryLang,
                title: primaryContent.name,
                text: primaryContent.text,
                audioUrl: primaryContent.audioUrl || undefined,
                pictures: pictures.length > 0 ? pictures : undefined,
                type: "INVISIBLE",
                triggerMode: "IMAGE_SCAN",
                scannableObjectId: selectedObjectId || undefined,
                position,
                latitude: waypoint?.point?.latitude || waypoint?.latitude || 0,
                longitude: waypoint?.point?.longitude || waypoint?.longitude || 0,
                // Include all localized content
                localizedContent: localizedContent,
            });

            // Update track's available languages on save
            updateTrackData("availableLanguages", availableLanguages);

            navigateToNext();
        } catch (error) {
            console.error("Error updating story:", error);
        } finally {
            setLoading(false);
        }
    };

    const currentContent = localizedContent[currentLanguage] || { name: "", text: "", audioUrl: "" };
    const selectedObject = objects.find((obj) => obj.id === selectedObjectId);
    const selectedObjectName = selectedObject ? getObjectLocalizedName(selectedObject) : "";

    // Check if form is valid - must have a name and at least one image
    const isFormValid = Object.values(localizedContent).some((c) => c.name.trim()) && pictures.length > 0;

    if (loadingData) {
        return (
            <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step}`}>
                <p>{t("loading")}</p>
            </div>
        );
    }

    return (
        <div className={`step-container ${stepStyles.stepContainer} ${stepStyles.step} ${formStyles.root}`}>
            {/* Breadcrumb */}
            <div className={formStyles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/stories`}>{t("content")}</Link>
                <span>›</span>
                <Link to={`/create/tour/${linkId}/stories`}>{t("stories")}</Link>
                <span>›</span>
                <span>
                    {t("object")}: {selectedObjectName || t("selectObject")}
                </span>
                <span>›</span>
                <span className={formStyles.breadcrumbCurrent}>{t("modifyStory")}</span>
            </div>

            <TitleSubtitle title={t("modifyStory")} subtitle={t("updateStoryContent")} layout="entityForm" />

            <div className={stepStyles.stepDescription}>
                {/* Images */}
                <FormGroup title={t("images")} subtitle={t("imagesWillIllustrateStory")} required>
                    {pictures.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "10px" }}>
                            {pictures.map((pic, index) => (
                                <div key={index} style={{ position: "relative" }}>
                                    <img
                                        src={pic}
                                        alt={`${t("picture")} ${index + 1}`}
                                        style={{
                                            width: "100px",
                                            height: "100px",
                                            objectFit: "cover",
                                            borderRadius: "8px",
                                        }}
                                    />
                                    <button
                                        onClick={() => handleRemovePicture(index)}
                                        type="button"
                                        style={{
                                            position: "absolute",
                                            top: "-8px",
                                            right: "-8px",
                                            background: "#ff4757",
                                            color: "white",
                                            border: "2px solid white",
                                            borderRadius: "50%",
                                            width: "24px",
                                            height: "24px",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            lineHeight: "1",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            padding: 0,
                                            boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                            zIndex: 10,
                                        }}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handlePictureUpload}
                        placeholder={uploadingPicture ? t("uploading") : t("dragAndDropOrClick")}
                        disabled={uploadingPicture}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                    />
                    {pictures.length === 0 && (
                        <p style={{ color: "#ff4757", fontSize: "13px", marginTop: "4px" }}>{t("imageRequired")}</p>
                    )}
                </FormGroup>

                {/* Position */}
                <FormGroup title={t("position")} subtitle={t("positionOrderInApp")} required>
                    <TextInput
                        placeholder="1"
                        value={position.toString()}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                            setPosition(parseInt(e.target.value) || 1)
                        }
                        size="small"
                        type="number"
                    />
                    {isDuplicatePosition && (
                        <p style={{ color: "#f39c12", fontSize: "13px", marginTop: "4px" }}>
                            ⚠️{" "}
                            {t("positionAlreadyUsed") ||
                                "This position is already used by another story for this object. Consider using a different position."}
                        </p>
                    )}
                </FormGroup>

                {/* Object selection */}
                <FormGroup title={t("scannableObject")} subtitle={t("selectObjectForStory")}>
                    <Dropdown
                        label=""
                        value={selectedObjectId}
                        options={objectOptions}
                        onChange={setSelectedObjectId}
                        placeholder={t("selectObject")}
                        loading={false}
                        error={false}
                    />
                </FormGroup>

                {/* Localized Content with Language Tabs */}
                <FormGroup
                    title={t("contentByLanguage")}
                    subtitle={t("contentByLanguageDescription")}
                    style={{ marginTop: "32px" }}
                >
                    <Box
                        sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                            gap: "8px",
                        }}
                    >
                        {/* Language tabs with remove button */}
                        <div style={{ display: "flex", alignItems: "center", gap: "4px", flexWrap: "wrap" }}>
                            {availableLanguages.map((lang, index) => (
                                <div
                                    key={lang}
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "4px",
                                        padding: "8px 12px",
                                        borderBottom:
                                            activeLanguageTab === index
                                                ? "2px solid var(--green-600)"
                                                : "2px solid transparent",
                                        cursor: "pointer",
                                        color: activeLanguageTab === index ? "var(--green-600)" : "#666",
                                        fontWeight: 500,
                                        fontSize: "14px",
                                        transition: "all 0.2s ease",
                                    }}
                                    onClick={() => handleLanguageTabChange(index)}
                                >
                                    <span>{getLanguageName(lang)}</span>
                                    {/* Show remove button only if not creation language */}
                                    {lang !== creationLanguage && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemoveLanguage(lang);
                                            }}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#999",
                                                cursor: "pointer",
                                                padding: "0 4px",
                                                fontSize: "14px",
                                                lineHeight: 1,
                                            }}
                                            title={t("removeLanguage")}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>

                        {/* Add language dropdown */}
                        {unusedLanguages.length > 0 && (
                            <div style={{ position: "relative", marginLeft: "8px" }}>
                                {showLanguageSelector ? (
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <select
                                            onChange={(e) => {
                                                if (e.target.value) {
                                                    handleAddLanguage(e.target.value);
                                                }
                                            }}
                                            style={{
                                                padding: "8px 12px",
                                                borderRadius: "4px",
                                                border: "2px solid var(--green-600)",
                                                fontSize: "14px",
                                                cursor: "pointer",
                                                background: "white",
                                                color: "#1a1a2e",
                                                fontWeight: 500,
                                                outline: "none",
                                                transition: "all 0.2s ease",
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.borderColor = "#2ab8c8";
                                                e.target.style.boxShadow = "0 0 0 3px rgba(58, 202, 218, 0.1)";
                                            }}
                                            onBlur={(e) => {
                                                e.target.style.borderColor = "var(--green-600)";
                                                e.target.style.boxShadow = "none";
                                            }}
                                            defaultValue=""
                                        >
                                            <option value="" disabled style={{ color: "#999" }}>
                                                {t("selectLanguage")}
                                            </option>
                                            {unusedLanguages.map((lang: any) => (
                                                <option key={lang.code} value={lang.code}>
                                                    {lang.label}
                                                </option>
                                            ))}
                                        </select>
                                        <button
                                            onClick={() => setShowLanguageSelector(false)}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                color: "#999",
                                                cursor: "pointer",
                                                fontSize: "14px",
                                                padding: "8px 4px",
                                            }}
                                        >
                                            {t("cancel")}
                                        </button>
                                    </div>
                                ) : (
                                    <button
                                        onClick={() => setShowLanguageSelector(true)}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            color: "var(--green-600)",
                                            cursor: "pointer",
                                            fontSize: "14px",
                                            fontWeight: 500,
                                            padding: "8px 0",
                                        }}
                                    >
                                        + {t("addLanguage")}
                                    </button>
                                )}
                            </div>
                        )}
                    </Box>

                    {/* Name */}
                    <div style={{ marginBottom: "24px" }}>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "600", fontSize: "14px" }}>
                            {t("name")} <span style={{ color: "#ff4757" }}>*</span>
                        </label>
                        <p style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
                            {t("enterNameIn", { language: getLanguageName(currentLanguage) })}
                        </p>
                        <TextInput
                            placeholder={t("enterStoryName")}
                            value={currentContent.name}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                handleContentChange("name", e.target.value)
                            }
                            size="medium"
                        />
                    </div>

                    {/* Text */}
                    <div style={{ marginBottom: "24px" }}>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "600", fontSize: "14px" }}>
                            {t("text")}
                        </label>
                        <p style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
                            {t("enterTextIn", { language: getLanguageName(currentLanguage) })}
                        </p>
                        <TextInput
                            placeholder={t("enterStoryDescription")}
                            value={currentContent.text}
                            onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                                handleContentChange("text", e.target.value)
                            }
                            size="big"
                            numberOfLines={6}
                            style={{
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                padding: "12px",
                                width: "100%",
                                boxSizing: "border-box",
                            }}
                        />

                        {/* Add from projects */}
                        <div style={{ marginTop: "16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                <span style={{ color: "var(--green-600)", fontSize: "16px" }}>✓</span>
                                <span style={{ fontSize: "14px", fontWeight: 500 }}>{t("addFromProjects")}</span>
                            </div>
                            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                                <div style={{ minWidth: "200px" }}>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectId}
                                        options={projectOptions}
                                        onChange={(val) => {
                                            setSelectedProjectId(val);
                                            setSelectedProjectStoryId("");
                                        }}
                                        placeholder={t("selectProject")}
                                        loading={loadingProjects}
                                        error={false}
                                    />
                                </div>
                                <div style={{ minWidth: "200px" }}>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectStoryId}
                                        options={storyOptions}
                                        onChange={setSelectedProjectStoryId}
                                        placeholder={t("selectStory")}
                                        loading={false}
                                        error={false}
                                        disabled={!selectedProjectId}
                                    />
                                </div>
                                {selectedProjectStoryId && (
                                    <button
                                        onClick={handleImportTextFromProject}
                                        type="button"
                                        style={{
                                            padding: "8px 16px",
                                            background: "var(--green-600)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {t("import")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Audio */}
                    <div style={{ marginBottom: "24px" }}>
                        <label style={{ display: "block", marginBottom: "4px", fontWeight: "600", fontSize: "14px" }}>
                            {t("audio")}
                        </label>
                        <p style={{ fontSize: "13px", color: "#666", marginBottom: "8px" }}>
                            {t("insertAudioIn", { language: getLanguageName(currentLanguage) })}
                        </p>
                        {currentContent.audioUrl && (
                            <div style={{ marginBottom: "10px" }}>
                                <audio controls src={currentContent.audioUrl} style={{ width: "100%" }} />
                                <button
                                    onClick={() => handleContentChange("audioUrl", "")}
                                    type="button"
                                    style={{
                                        marginTop: "8px",
                                        padding: "6px 12px",
                                        background: "#ff4757",
                                        color: "white",
                                        border: "none",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                    }}
                                >
                                    {t("remove")} {t("audio")}
                                </button>
                            </div>
                        )}
                        <FileInput
                            key={`audio-${currentLanguage}`}
                            onFileSelected={handleAudioUpload}
                            placeholder={uploadingAudio ? t("uploading") : t("dragAndDropOrClick")}
                            disabled={uploadingAudio}
                            acceptedFileTypes={{ "audio/*": [".mp3", ".wav", ".m4a"] }}
                        />

                        {/* Add audio from projects */}
                        <div style={{ marginTop: "16px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                                <span style={{ color: "var(--green-600)", fontSize: "16px" }}>✓</span>
                                <span style={{ fontSize: "14px", fontWeight: 500 }}>{t("addFromProjects")}</span>
                            </div>
                            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
                                <div style={{ minWidth: "200px" }}>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectIdForAudio}
                                        options={projectOptions}
                                        onChange={(val) => {
                                            setSelectedProjectIdForAudio(val);
                                            setSelectedProjectStoryIdForAudio("");
                                        }}
                                        placeholder={t("selectProject")}
                                        loading={loadingProjects}
                                        error={false}
                                    />
                                </div>
                                <div style={{ minWidth: "200px" }}>
                                    <Dropdown
                                        label=""
                                        value={selectedProjectStoryIdForAudio}
                                        options={storyOptionsForAudio}
                                        onChange={setSelectedProjectStoryIdForAudio}
                                        placeholder={t("selectStory")}
                                        loading={false}
                                        error={false}
                                        disabled={!selectedProjectIdForAudio}
                                    />
                                </div>
                                {selectedProjectStoryIdForAudio && (
                                    <button
                                        onClick={handleImportAudioFromProject}
                                        type="button"
                                        style={{
                                            padding: "8px 16px",
                                            background: "var(--green-600)",
                                            color: "white",
                                            border: "none",
                                            borderRadius: "6px",
                                            cursor: "pointer",
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {t("import")}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </FormGroup>
            </div>

            {/* Action buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={t("cancel")}
                    onClick={handleCancel}
                    variant="cancel"
                    shape="pill"
                    className={stepStyles.footerBtn}
                />
                <Button
                    label={loading ? t("saving") : t("save")}
                    onClick={handleSave}
                    disabled={!isFormValid || loading || uploadingPicture || uploadingAudio}
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${stepStyles.footerBtn}`}
                />
            </div>
        </div>
    );
};

export default ModifyStory;
